import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, X, GripVertical } from "lucide-react";

function Pad() {
  const [expr, setExpr] = useState("");
  const [result, setResult] = useState("");
  const press = (v) => setExpr((e) => e + v);
  const clear = () => { setExpr(""); setResult(""); };
  const back = () => setExpr((e) => e.slice(0, -1));
  const equals = () => {
    if (!/^[0-9+\-*/.()%\s]*$/.test(expr)) { setResult("Error"); return; }
    try {
      // eslint-disable-next-line no-new-func
      const r = Function(`"use strict";return (${expr.replace(/%/g, "/100")})`)();
      setResult(r === undefined || r === null || isNaN(r) ? "Error" : String(Math.round(r * 1000) / 1000));
    } catch (e) { setResult("Error"); }
  };
  const keys = ["7", "8", "9", "/", "4", "5", "6", "*", "1", "2", "3", "-", "0", ".", "%", "+"];
  return (
    <div data-testid="float-calc-pad">
      <div className="bg-brand-ink rounded-2xl p-3 mb-3 text-right">
        <p className="text-white/60 text-xs h-4 truncate">{expr || "0"}</p>
        <p className="text-white font-outfit text-2xl font-extrabold truncate" data-testid="float-calc-display">{result || "0"}</p>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        <button onClick={clear} className="col-span-2 py-2.5 rounded-xl bg-destructive/10 text-destructive font-bold text-sm">C</button>
        <button onClick={back} className="py-2.5 rounded-xl bg-muted font-bold text-sm">⌫</button>
        <button onClick={equals} data-testid="float-calc-equals" className="row-span-5 rounded-xl bg-brand-terracotta text-white font-bold text-lg">=</button>
        {keys.map((k) => (
          <button key={k} data-testid={`float-calc-key-${k}`} onClick={() => press(k)}
            className={`py-2.5 rounded-xl font-bold text-sm ${"+-*/%".includes(k) ? "bg-brand-sage/15 text-brand-sageDark" : "bg-stone-100 text-brand-ink"}`}>{k}</button>
        ))}
      </div>
    </div>
  );
}

export default function FloatingCalculator() {
  const [open, setOpen] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (e.detail === "calc") setOpen(true); };
    window.addEventListener("clingo-open", h);
    return () => window.removeEventListener("clingo-open", h);
  }, []);

  return (
    <>
      {!open && (
        <motion.button
          data-testid="float-calc-toggle"
          onClick={() => setOpen(true)}
          drag
          dragMomentum={false}
          dragElastic={0}
          whileTap={{ scale: 0.92 }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="fixed bottom-24 right-6 z-[60] w-14 h-14 hidden md:flex items-center justify-center rounded-full bg-brand-terracotta text-white shadow-xl shadow-brand-terracotta/30 cursor-grab active:cursor-grabbing"
          title="Calculadora (arrástrame)"
        >
          <Calculator size={22} />
        </motion.button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            ref={dragRef}
            drag
            dragMomentum={false}
            dragElastic={0}
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            data-testid="float-calc-panel"
            className="fixed bottom-24 right-6 z-[60] w-60 bg-white rounded-3xl border border-stone-100 shadow-2xl p-3.5"
          >
            <div className="flex items-center justify-between mb-2 cursor-grab active:cursor-grabbing">
              <div className="flex items-center gap-1.5 text-brand-ink/60">
                <GripVertical size={16} />
                <span className="text-xs font-bold uppercase tracking-wide">Calculadora</span>
              </div>
              <button data-testid="float-calc-close" onClick={() => setOpen(false)}
                className="p-1 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all">
                <X size={16} />
              </button>
            </div>
            <Pad />
            <p className="text-[10px] text-muted-foreground mt-2 text-center">Arrastra para moverla al lado que prefieras</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
